import * as React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography } from "@mui/material";
import { ApplicationSummary, ApplicationsService } from "gen/clients";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { Form, Formik } from "formik";
import { SelectOption } from "@mui/base";
import { useQuery } from "react-query";
import { ApplicationId } from "types/ApplicationId";
import * as Yup from "yup";

interface Props {
  availableApplications: ApplicationSummary[];
  onClose: () => void;
  onAdd: (applicationId: string, presetId?: string) => void;
}

enum FieldName {
  applicationId = "applicationId",
  presetId = "presetId"
}

interface FormValues {
  [FieldName.applicationId]: string;
  [FieldName.presetId]: string;
}

const AddApplicationDialog: React.FC<Props> = ({ availableApplications, onClose, onAdd }) => {
  const [selectedApplicationId, setSelectedApplicationId] = React.useState<string>("");

  const { data: selectedApplication, isLoading: isLoadingApplication } = useQuery(
    ["getApplication", selectedApplicationId],
    {
      queryFn: () => ApplicationsService.getApplication({ id: selectedApplicationId }),
      enabled: !!selectedApplicationId,
      cacheTime: 0
    }
  );

  const applicationOptions: SelectOption<string>[] = React.useMemo(
    () =>
      availableApplications.map(app => ({
        label: app.name || app.id,
        value: app.id
      })),
    [availableApplications]
  );

  const presetOptions: SelectOption<string>[] = React.useMemo(() => {
    if (!selectedApplication?.settingPresets) {
      return [];
    }

    return selectedApplication.settingPresets.map(preset => ({
      label: preset.name,
      value: preset.id
    }));
  }, [selectedApplication]);

  const validationSchema = Yup.object().shape({
    [FieldName.applicationId]: Yup.string().required("Application is required"),
    [FieldName.presetId]: Yup.string().when(FieldName.applicationId, {
      is: ApplicationId.machineTranslation,
      then: schema => schema.required("Preset is required for Machine Translation"),
      otherwise: schema => schema
    })
  });

  const initialValues: FormValues = {
    [FieldName.applicationId]: "",
    [FieldName.presetId]: ""
  };

  const handleSubmit = React.useCallback(
    (values: FormValues) => {
      onAdd(values.applicationId, values.presetId || undefined);
    },
    [onAdd]
  );

  const handleApplicationChange = React.useCallback((applicationId: string) => {
    setSelectedApplicationId(applicationId);
  }, []);

  const showPresetSelection = selectedApplicationId === ApplicationId.machineTranslation;

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Application</DialogTitle>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ isSubmitting, isValid, dirty }) => (
          <Form>
            <DialogContent>
              <Stack spacing={3}>
                <SelectOneField
                  name={FieldName.applicationId}
                  label="Application"
                  options={applicationOptions}
                  required
                  onChange={handleApplicationChange}
                />

                {showPresetSelection && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Machine Translation requires selecting a preset configuration.
                    </Typography>
                    <SelectOneField
                      name={FieldName.presetId}
                      label="Application Preset"
                      options={presetOptions}
                      required
                      isLoading={isLoadingApplication}
                      disabled={!selectedApplicationId || isLoadingApplication}
                    />
                  </>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={!isValid || !dirty || isSubmitting}>
                Add Application
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export { AddApplicationDialog };
